import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Switch, Typography, Spin } from 'antd';

const { Text } = Typography;

/**
 * ChromaticAberrationEditorPanel — RGB channel shift effect.
 * Splits image into R, G, B channels and offsets them.
 */
const ChromaticAberrationEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    const [shiftAmount, setShiftAmount] = useState(5);
    const [angle, setAngle] = useState(0);
    const [redOffsetX, setRedOffsetX] = useState(null); // null = auto from angle
    const [redOffsetY, setRedOffsetY] = useState(null);
    const [blueOffsetX, setBlueOffsetX] = useState(null);
    const [blueOffsetY, setBlueOffsetY] = useState(null);
    const [radial, setRadial] = useState(false);
    const [radialStrength, setRadialStrength] = useState(1.0);
    const [blurChannels, setBlurChannels] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { sourceImgRef.current = img; setLoading(false); };
        img.onerror = () => setLoading(false);
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderChromatic();
    }, [visible, shiftAmount, angle, redOffsetX, redOffsetY, blueOffsetX, blueOffsetY, radial, radialStrength, blurChannels, loading]);

    const renderChromatic = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const shift = shiftAmount * scale;
        const angleRad = (angle * Math.PI) / 180;
        const rOx = redOffsetX !== null ? redOffsetX * scale : Math.cos(angleRad) * shift;
        const rOy = redOffsetY !== null ? redOffsetY * scale : Math.sin(angleRad) * shift;
        const bOx = blueOffsetX !== null ? blueOffsetX * scale : -Math.cos(angleRad) * shift;
        const bOy = blueOffsetY !== null ? blueOffsetY * scale : -Math.sin(angleRad) * shift;

        if (worker) {
            const generation = ++generationRef.current;
            // Source pixels (possibly blurred)
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            if (blurChannels && shift > 2) tmpCtx.filter = `blur(${Math.max(0.5, shift * 0.15)}px)`;
            tmpCtx.drawImage(img, 0, 0, w, h);
            tmpCtx.filter = 'none';
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            // Alpha pixels (no blur)
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w; alphaCanvas.height = h;
            alphaCanvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const alphaBuf = alphaCanvas.getContext('2d').getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    preview.width = ev.data.width; preview.height = ev.data.height;
                    preview.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderChromatic(); }
            };

            worker.postMessage({
                type: 'chromatic', srcPixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, rOx, rOy, bOx, bOy,
                radial, radialStrength, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(previewCanvasRef.current, 800); }
                finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderChromatic(); }
                }
            });
        }
    }, [shiftAmount, angle, redOffsetX, redOffsetY, blueOffsetX, blueOffsetY, radial, radialStrength, blurChannels]);

    const doRender = (canvas, maxDim) => {
        const img = sourceImgRef.current;
        if (!img || !canvas) return;

        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const shift = shiftAmount * scale;

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Calculate offsets
        const angleRad = (angle * Math.PI) / 180;
        const rOx = redOffsetX !== null ? redOffsetX * scale : Math.cos(angleRad) * shift;
        const rOy = redOffsetY !== null ? redOffsetY * scale : Math.sin(angleRad) * shift;
        const bOx = blueOffsetX !== null ? blueOffsetX * scale : -Math.cos(angleRad) * shift;
        const bOy = blueOffsetY !== null ? blueOffsetY * scale : -Math.sin(angleRad) * shift;

        // Draw original to get pixel data
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = w;
        tempCanvas.height = h;
        const tempCtx = tempCanvas.getContext('2d');

        if (blurChannels && shift > 2) {
            tempCtx.filter = `blur(${Math.max(0.5, shift * 0.15)}px)`;
        }
        tempCtx.drawImage(img, 0, 0, w, h);
        tempCtx.filter = 'none';

        const srcData = tempCtx.getImageData(0, 0, w, h).data;

        // Also get clean alpha from original (no blur)
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = w;
        alphaCanvas.height = h;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.drawImage(img, 0, 0, w, h);
        const alphaData = alphaCtx.getImageData(0, 0, w, h).data;

        const outData = ctx.createImageData(w, h);
        const out = outData.data;

        const cx = w / 2, cy = h / 2;
        const maxDist = Math.sqrt(cx * cx + cy * cy);

        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const idx = (y * w + x) * 4;

                let rMul = 1, bMul = 1;
                if (radial) {
                    const dx = x - cx, dy = y - cy;
                    const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;
                    rMul = dist * radialStrength;
                    bMul = dist * radialStrength;
                }

                // Red channel — shifted
                const rsx = Math.round(x - rOx * rMul);
                const rsy = Math.round(y - rOy * rMul);
                if (rsx >= 0 && rsx < w && rsy >= 0 && rsy < h) {
                    out[idx] = srcData[(rsy * w + rsx) * 4];
                } else {
                    out[idx] = srcData[idx];
                }

                // Green channel — stays centered
                out[idx + 1] = srcData[idx + 1];

                // Blue channel — shifted opposite
                const bsx = Math.round(x - bOx * bMul);
                const bsy = Math.round(y - bOy * bMul);
                if (bsx >= 0 && bsx < w && bsy >= 0 && bsy < h) {
                    out[idx + 2] = srcData[(bsy * w + bsx) * 4 + 2];
                } else {
                    out[idx + 2] = srcData[idx + 2];
                }

                // Alpha — use original
                out[idx + 3] = alphaData[idx + 3];
            }
        }

        ctx.putImageData(outData, 0, 0);
    };

    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const w = img.width, h = img.height;
        const shift = shiftAmount; // full res, no scale
        const angleRad = (angle * Math.PI) / 180;
        const rOx = redOffsetX !== null ? redOffsetX : Math.cos(angleRad) * shift;
        const rOy = redOffsetY !== null ? redOffsetY : Math.sin(angleRad) * shift;
        const bOx = blueOffsetX !== null ? blueOffsetX : -Math.cos(angleRad) * shift;
        const bOy = blueOffsetY !== null ? blueOffsetY : -Math.sin(angleRad) * shift;

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            if (blurChannels && shift > 2) tmpCtx.filter = `blur(${Math.max(0.5, shift * 0.15)}px)`;
            tmpCtx.drawImage(img, 0, 0, w, h);
            tmpCtx.filter = 'none';
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w; alphaCanvas.height = h;
            alphaCanvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const alphaBuf = alphaCanvas.getContext('2d').getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outCanvas = document.createElement('canvas');
                outCanvas.width = ev.data.width; outCanvas.height = ev.data.height;
                outCanvas.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'chromatic', srcPixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h, rOx, rOy, bOx, bOy,
                radial, radialStrength, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            const fullCanvas = document.createElement('canvas');
            doRender(fullCanvas, Math.max(img.width, img.height));
            onApply?.(fullCanvas.toDataURL('image/png'));
        }
    }, [shiftAmount, angle, redOffsetX, redOffsetY, blueOffsetX, blueOffsetY, radial, radialStrength, blurChannels, onApply]);

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#60a5fa' }, rail: { background: '#30363d' }, handle: { borderColor: '#60a5fa', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#60a5fa', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(1) : value}
            </Text>
        </div>
    );

    const toggleRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <Switch checked={value} onChange={onChange} size="small" style={{ background: value ? '#60a5fa' : '#30363d' }} />
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 110, display: 'flex', background: 'rgba(13, 17, 23, 0.95)', pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" /><Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', border: '1px solid #30363d' }} />
                )}
            </div>

            <div style={{ width: 310, flexShrink: 0, borderLeft: '1px solid #30363d', display: 'flex', flexDirection: 'column', background: '#0d1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #30363d', background: '#161b22', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>◎</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Chromatic Aberration</span>
                    </div>
                    <Button type="text" size="small" onClick={onClose} style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <Text style={{ color: '#60a5fa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Shift</Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Amount', 0, 30, shiftAmount, setShiftAmount)}
                            {sliderRow('Angle', 0, 360, angle, setAngle)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#60a5fa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Options</Text>
                        <div style={{ marginTop: 8 }}>
                            {toggleRow('Radial', radial, setRadial)}
                            {radial && sliderRow('Radial Str.', 0, 3, radialStrength, (v) => setRadialStrength(v), 0.1)}
                            {toggleRow('Blur Channels', blurChannels, setBlurChannels)}
                        </div>
                    </div>

                    <div>
                        <Text style={{ color: '#60a5fa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Manual Offsets</Text>
                        <Text style={{ color: '#6e7681', fontSize: 10, display: 'block', marginTop: 4 }}>Override auto angle-based offsets</Text>
                        <div style={{ marginTop: 8 }}>
                            <Text style={{ color: '#ef4444', fontSize: 10, fontWeight: 600 }}>RED</Text>
                            {sliderRow('Offset X', -30, 30, redOffsetX ?? Math.round(Math.cos(angle * Math.PI / 180) * shiftAmount), (v) => setRedOffsetX(v))}
                            {sliderRow('Offset Y', -30, 30, redOffsetY ?? Math.round(Math.sin(angle * Math.PI / 180) * shiftAmount), (v) => setRedOffsetY(v))}
                            <Text style={{ color: '#3b82f6', fontSize: 10, fontWeight: 600, marginTop: 4, display: 'block' }}>BLUE</Text>
                            {sliderRow('Offset X', -30, 30, blueOffsetX ?? -Math.round(Math.cos(angle * Math.PI / 180) * shiftAmount), (v) => setBlueOffsetX(v))}
                            {sliderRow('Offset Y', -30, 30, blueOffsetY ?? -Math.round(Math.sin(angle * Math.PI / 180) * shiftAmount), (v) => setBlueOffsetY(v))}
                            {(redOffsetX !== null || blueOffsetX !== null) && (
                                <Button size="small" type="text" onClick={() => { setRedOffsetX(null); setRedOffsetY(null); setBlueOffsetX(null); setBlueOffsetY(null); }}
                                    style={{ color: '#6e7681', fontSize: 10, padding: '2px 6px', marginTop: 4 }}>
                                    Reset to Auto
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ padding: '12px 14px', borderTop: '1px solid #30363d', display: 'flex', gap: 8, flexShrink: 0, background: '#161b22' }}>
                    <Button onClick={onClose} style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>Cancel</Button>
                    <Button type="primary" onClick={handleApply} style={{ flex: 1, background: '#60a5fa', borderColor: '#60a5fa' }}>Apply</Button>
                </div>
            </div>
        </div>
    );
};

export default ChromaticAberrationEditorPanel;
